/**
 * Draggable resize handle that sits on a rail's inner edge.
 *
 * Props:
 *   • side          'left' | 'right' — which rail this handle belongs to
 *   • currentWidth  number (px) — current rail width, used as the drag baseline
 *   • onResize      (px: number) => void — called continuously during drag
 *   • disabled      boolean — when true (e.g. rail collapsed), handle is inert
 *
 * Visuals:
 *   • 4px wide bar, full rail height, semi-transparent by default
 *   • Hover/drag → solid emerald accent + `col-resize` cursor
 *   • Positioned absolute on the inner edge (right edge of left rail,
 *     left edge of right rail)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function RailResizeHandle({ side, currentWidth, onResize, disabled }) {
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(currentWidth);

  const onMouseMove = useCallback((e) => {
    const dx = e.clientX - startXRef.current;
    // Left rail grows when dragging right (positive dx).
    // Right rail grows when dragging left (negative dx).
    const next = side === 'left'
      ? startWidthRef.current + dx
      : startWidthRef.current - dx;
    onResize(next);
  }, [side, onResize]);

  const onMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  // Attach window listeners only while dragging
  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    // Disable text selection / iframe interception during drag
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = prev;
      document.body.style.cursor = '';
    };
  }, [dragging, onMouseMove, onMouseUp]);

  const onMouseDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = currentWidth;
    setDragging(true);
  };

  const onDoubleClick = () => {
    if (disabled) return;
    onResize(320); // reset to default
  };

  if (disabled) return null;

  return (
    <div
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
      data-testid={`rail-${side}-resize-handle`}
      title="Drag to resize  ·  Double-click to reset to 320 px"
      className={`absolute top-0 bottom-0 ${side === 'left' ? '-right-1.5' : '-left-1.5'} w-3 cursor-col-resize z-20 group flex items-center justify-center`}
    >
      <div className={`h-12 w-0.5 rounded-full transition-colors ${dragging ? 'bg-emerald-500' : 'bg-slate-200 group-hover:bg-emerald-400'}`} />
    </div>
  );
}

export default RailResizeHandle;
